// utils/messageDisplay.ts
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { MessageContent } from "../generated/ln2/message-content";
import { LiftType } from "../../interfaces/Lift.interface";

import { BitMaskMessageT } from "../generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../generated/ln2/one-hot-message";

import { Traffic } from "../generated/traffic/traffic";
import { Usage } from "../generated/usage/usage";
import { Service } from "../generated/service/service";
import { Control } from "../generated/control/control";
import { Fault } from "../generated/fault/fault";
import { GroupSupervisoryStatus } from "../generated/group-supervisory-status/group-supervisory-status";
import { GroupControl } from "../generated/group-control/group-control";
import { ControllerInfoContent } from "../generated/controller-info/controller-info-content";
import { Status } from "../generated/status/status";

function enumName<E extends Record<string, string | number>>(
  enm: E,
  value?: number | null,
): string {
  if (value == null) return "--";
  const name = (enm as any)[value] as string | undefined;
  return name ?? String(value);
}

function prettyFlatbuffersName(raw: string): string {
  const beforeUnderscore = raw.split("_")[0];
  return beforeUnderscore.replace(/Message$/, "");
}

export function contentTypeDisplayName(value?: EncodedMessageContent | null): string {
  if (value == null || value === EncodedMessageContent.NONE) return "--";
  return prettyFlatbuffersName(enumName(EncodedMessageContent, value));
}

export function messageTypeDisplayName(value?: MessageContent | null): string {
  if (value == null || value === MessageContent.NONE) return "--";
  const name = (MessageContent as any)[value] as string | undefined;
  if (name) return prettyFlatbuffersName(name);
  return String(value);
}

// ---------- helpers ----------
function enumLabel(enm: any, value?: number | null): string {
  if (value == null) return "--";
  return enm[value] ?? String(value);
}

// FlatBuffer variant wrappers for the leaf enum aren't always named `message`.
// Status nests under `.status`, Usage under `.usage`, Traffic under `.traffic`,
// Fault under `.fault`, etc. At each level: prefer `.message` if present, then
// fall back to any sibling object property that holds a `{ message: number }`
// leaf, and finally recurse into the first object-valued sibling.
function pickNextLevel(cur: any): { leaf: number | null; next: any } {
  let leaf: number | null = null;
  let next: any = null;
  const keys = Object.keys(cur);
  for (let i = 0; i < keys.length; i += 1) {
    const value = cur[keys[i]];
    if (value && typeof value === "object") {
      if (leaf === null && typeof value.message === "number") {
        leaf = value.message;
      } else if (next === null) {
        next = value;
      }
    }
  }
  return { leaf, next };
}

function extractInnerMessageCode(content: any): number | null {
  if (!content) return null;

  let cur: any = content;
  for (let i = 0; i < 6; i += 1) {
    if (cur == null) return null;
    if (typeof cur.message === "number") return cur.message;
    if (typeof cur !== "object") return null;

    if (cur.message && typeof cur.message === "object") {
      cur = cur.message;
    } else {
      const { leaf, next } = pickNextLevel(cur);
      if (leaf !== null) return leaf;
      if (next === null) return null;
      cur = next;
    }
  }

  return null;
}

// Maps the outer MessageContent family to the leaf enum used to label its
// inner code. The envelope (BitMask vs OneHot) doesn't dictate the leaf enum —
// e.g., Traffic can arrive as either, and Usage can arrive as either. So the
// lookup is by messageType only.
function enumForMessageType(messageType: MessageContent): any | null {
  switch (messageType) {
    case MessageContent.Status_StatusMessage:
      return Status;
    case MessageContent.Traffic_TrafficMessage:
      return Traffic;
    case MessageContent.Usage_UsageMessage:
      return Usage;
    case MessageContent.Service_ServiceMessage:
      return Service;
    case MessageContent.Control_ControlMessage:
      return Control;
    case MessageContent.Fault_FaultMessage:
      return Fault;
    case MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage:
      return GroupSupervisoryStatus;
    case MessageContent.GroupControl_GroupControlMessage:
      return GroupControl;
    case MessageContent.ControllerInfo_ControllerInfoMessage:
      return ControllerInfoContent;
    default:
      return null;
  }
}

// ---------- main ----------
export function msgInnerDisplayName(args: {
  liftType: LiftType;
  contentType?: EncodedMessageContent | null;
  messageType?: MessageContent | MessageContent | null;
  content?: BitMaskMessageT | OneHotMessageT | null;
}): string {
  const { liftType: _liftType, contentType, messageType, content } = args;

  if (!contentType || !messageType || !content) return "--";

  const wrapper =
    contentType === EncodedMessageContent.OneHotMessage
      ? (content as OneHotMessageT)
      : (content as BitMaskMessageT);
  const code = extractInnerMessageCode(wrapper.message);
  if (code == null) return "--";

  const leafEnum = enumForMessageType(messageType);
  return leafEnum ? enumLabel(leafEnum, code) : String(code);
}

// Combines envelope (BitMask/OneHot) + outer message family (Status/Usage/...)
// + inner enum leaf (Connected/Up/...) into a single double-colon path.
// Used by the Terminal view's "Message" column to surface the full
// FrontEndMessage shape that previously only appeared in console logs.
export function msgFullDisplayName(args: {
  liftType: LiftType;
  contentType?: EncodedMessageContent | null;
  messageType?: MessageContent | null;
  content?: BitMaskMessageT | OneHotMessageT | null;
}): string {
  const envelope = contentTypeDisplayName(args.contentType);
  const family = messageTypeDisplayName(args.messageType);
  const inner = msgInnerDisplayName(args);
  if (envelope === "--" && family === "--" && inner === "--") return "--";
  return [envelope, family, inner].filter((p) => p && p !== "--").join("::");
}

// Extract the auxiliary scalar payload that travels alongside the leaf enum.
// NormalTraffic carries `position` (the floor), valued faults carry a numeric
// `value`, etc. Returns null when the leaf enum fully describes the message
// (Service::Auto, Usage::Up, Status::Connected — none have extra payload).
function findScalarPayload(content: any): number | null {
  if (!content || typeof content !== "object") return null;

  const PAYLOAD_KEYS = ["position", "value", "floor"];
  let cur: any = content;
  for (let depth = 0; depth < 8; depth += 1) {
    if (cur == null || typeof cur !== "object") return null;
    for (let i = 0; i < PAYLOAD_KEYS.length; i += 1) {
      const key = PAYLOAD_KEYS[i];
      if (typeof cur[key] === "number") return cur[key];
    }
    // Descend through `.message` if it points at another object — same path
    // extractInnerMessageCode walks, so we visit every wrapper level.
    if (cur.message && typeof cur.message === "object") {
      cur = cur.message;
    } else {
      return null;
    }
  }
  return null;
}

export function msgValueDisplayName(args: {
  contentType?: EncodedMessageContent | null;
  messageType?: MessageContent | null;
  content?: BitMaskMessageT | OneHotMessageT | null;
}): string {
  if (!args.content) return "--";
  const payload = findScalarPayload(args.content as any);
  return payload == null ? "--" : String(payload);
}
